import {
  Component,
  HostListener,
  inject,
  OnInit,
  PLATFORM_ID,
  signal,
  ViewContainerRef,
} from '@angular/core';
import {
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  mergeMap,
  Observable,
} from 'rxjs';
import { Store } from '@ngrx/store';
import { fromStore } from './core/store';
import { ModalService } from './core/services/modal.service';
import {
  ActivatedRoute,
  NavigationEnd,
  Params,
  Router,
  Scroll,
} from '@angular/router';
import { uiActions } from './core/store/ui';
import { AppSection, PublicAppSection } from './core/store/ui/ui.state';
import { authActions } from './core/store/auth';
import { ratesActions } from './core/store/rates';
import { User } from './core/models/entities/user';
import { eventTypesActions } from './core/store/event-types';
import { amenitiesActions } from './core/store/amenities';
import { layoutsActions } from './core/store/layouts';
import { ToastService } from './core/services/toast.service';
import { Toast } from './core/models/common/toast';
import { slideInTopAnimation } from './core/animations/slide-in-top.animation';
import { environment } from '../environments/environment';
import { roomsActions } from './core/store/rooms';
import { bookingsActions } from './core/store/bookings';
import { invoicingActions } from './core/store/invoicing';
import { usersActions } from './core/store/users';
import { BookingsScope } from './core/store/bookings/bookings.state';
import { invitesActions } from './core/store/invites';
import { Meta, Title } from '@angular/platform-browser';
import { buildingsActions } from './core/store/buildings';
import {
  selectAppSection,
  selectAuthToken,
  selectPublicVendor,
} from './core/store/reducers';
import { CookieService } from 'ngx-cookie-service';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { gsap } from 'gsap';
import { isPlatformBrowser } from '@angular/common';
import { register } from 'swiper/element/bundle';
import { VendorsService } from './core/services/vendors.service';
import * as Sentry from '@sentry/angular';
import { customFieldsActions } from './core/store/custom-fields';
import { DirtyTrackingService } from './core/services/dirty-tracking.service';

register();

gsap.registerPlugin(ScrollTrigger);

declare const gtag: Function;

@Component({
  selector: 'app-root',
  animations: [slideInTopAnimation],
  template: `
    <div
      class="fixed top-4 z-101 grid w-full grid-cols-1 space-y-4 px-4 md:top-10 md:left-1/3 md:w-1/3"
      >
      @for (toast of toasts; track toast; let i = $index) {
        <div @slideInTopAnimation>
          <app-toast
            [toast]="toast"
            (onDismiss)="toastService.dismiss(i)"
          ></app-toast>
        </div>
      }
    </div>
    <!-- Interface publique -->
    @if ((appSection$ | async) === AppSection.PUBLIC) {
      <div>
        @if (b2c() && currentVendor()) {
          <app-test-mode-header
            [vendor]="currentVendor()"
          ></app-test-mode-header>
        }
        @if (!hideNavbarAndFooter()) {
          <app-navbar></app-navbar>
        }
        <div
          class="public-theme"
        [ngClass]="{
          'mt-16 min-h-[80vh] lg:mt-20 xl:mt-[100px]': !hideNavbarAndFooter(),
        }"
          >
          <router-outlet></router-outlet>
        </div>
        @if (!hideNavbarAndFooter()) {
          <app-footer></app-footer>
        }
      </div>
    } @else {
      <app-manager-layout></app-manager-layout>
    }
    <!-- Inteface manager (admin) -->
    `,
  standalone: false,
})
export class AppComponent implements OnInit {
  currentVendor = this.store.selectSignal(selectPublicVendor);
  appSection$: Observable<AppSection> = this.store.select(selectAppSection);
  AppSection = AppSection;
  toasts: Toast[];
  b2c = signal(false);
  hideNavbarAndFooter = signal(false);
  platformId: object = inject(PLATFORM_ID);
  vendorsService = inject(VendorsService);
  dirtyTrackingService = inject(DirtyTrackingService);

  /**
   * Détecte la fermeture/rechargement de la page navigateur
   * Affiche un message de confirmation si des modifications non enregistrées existent
   */
  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: BeforeUnloadEvent): void {
    if (this.dirtyTrackingService.isDirty) {
      $event.preventDefault();
      $event.returnValue = true; // Pour les navigateurs modernes
    }
  }

  constructor(
    private store: Store,
    private modalService: ModalService,
    private viewContainerRef: ViewContainerRef,
    private router: Router,
    private route: ActivatedRoute,
    public toastService: ToastService,
    private title: Title,
    private cookieService: CookieService,
    private meta: Meta,
  ) {
    this.toasts = this.toastService.activeToasts;
    this.cookieService.deleteAll();
  }

  async ngOnInit() {
    this.loadCollections();

    if (this.store.selectSignal(selectAuthToken)())
      this.store.dispatch(authActions.findCurrentUser());
    this.store
      .select(fromStore.selectAuthUser)
      .pipe(
        distinctUntilChanged(
          (a, b) => a?.id === b?.id && a?.vendor?.id === b?.vendor?.id,
        ),
      )
      .subscribe((user: User | null) => {
        if (user) {
          Sentry.setUser({ id: user.id, email: user.email });

          this.store.dispatch(bookingsActions.findAllPending());
          this.store.dispatch(invoicingActions.findOpenCount());
          if (user.vendor) {
            this.store.dispatch(
              ratesActions.findAll({ vendorId: user.vendor.id }),
            );
            this.store.dispatch(
              buildingsActions.findAll({ vendorId: user.vendor.id }),
            );
            this.store.dispatch(
              customFieldsActions.findAll({ vendorId: user.vendor.id }),
            );
          }
        } else {
          this.store.dispatch(
            ratesActions.findAllSuccess({ rates: [], total: 0 }),
          );
          this.store.dispatch(
            roomsActions.findAllSuccess({ rooms: [], total: 0 }),
          );
          this.store.dispatch(
            bookingsActions.findAllSuccess({
              bookings: [],
              total: 0,
              scope: BookingsScope.All,
            }),
          );
          this.store.dispatch(
            bookingsActions.findAllPendingSuccess({ bookings: [], total: 0 }),
          );
          this.store.dispatch(
            invoicingActions.findAllSuccess({ bookings: [], total: 0 }),
          );
          this.store.dispatch(
            usersActions.findAllSuccess({ users: [], totalCount: 0 }),
          );
          this.store.dispatch(invitesActions.findAllSuccess({ invites: [] }));
        }
      });

    this.router.events
      .pipe(filter((event) => event instanceof Scroll))
      // @ts-ignore
      .subscribe((e: Scroll) => {
        if (!e.position && isPlatformBrowser(this.platformId)) {
          window.scrollTo({
            top: 0,
            behavior: 'instant',
          });
        }
      });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.route),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        mergeMap((route) => route.data),
      )
      .subscribe((routeData) => {
        this.b2c.set(!!routeData['b2c']);
        if (!this.b2c()) {
          this.store.dispatch(uiActions.setVendorSlug({ vendorSlug: null }));
        }
        this.hideNavbarAndFooter.set(!!routeData['hideNavbarAndFooter']);
        this.modalService.closeAllModals();

        this.store.dispatch(
          uiActions.setAppSection({
            appSection: routeData['manager']
              ? AppSection.MANAGER
              : AppSection.PUBLIC,
          }),
        );

        firstValueFrom(this.route.firstChild.params).then((params: Params) => {
          if (routeData['b2c'] && params['vendorSlug']) {
            this.store.dispatch(
              uiActions.setVendorSlug({ vendorSlug: params['vendorSlug'] }),
            );
          }

          if (
            routeData['b2c'] &&
            !this.router.url.startsWith('/louer-salle/demo')
          ) {
            this.store.dispatch(
              uiActions.setPublicAppSection({
                publicAppSection: PublicAppSection.B2C,
              }),
            );
          } else {
            this.store.dispatch(
              uiActions.setPublicAppSection({
                publicAppSection: PublicAppSection.B2B,
              }),
            );
          }
        });

        // Tracking Google Analytics
        if (environment.production) {
          this.router.events
            .pipe(
              distinctUntilChanged((previous: any, current: any) => {
                // Subscribe to any `NavigationEnd` events where the url has changed
                if (current instanceof NavigationEnd) {
                  return previous.url === current.url;
                }
                return true;
              }),
            )
            .subscribe(() => {
              if (typeof gtag !== 'undefined') {
                gtag('event', 'page_view', {
                  page_title: this.title.getTitle(),
                  page_location: location.href,
                  page_path: location.pathname,
                  send_to: 'G-NQMTRWQTZF',
                });
              }
            });
        }
      });

    this.modalService.watcher$.subscribe((event) => {
      if (event.type === 'open') {
        const componentRef = this.viewContainerRef.createComponent(
          event.modal.component,
          {
            index: event.modal.id,
          },
        );
        componentRef.instance.modalInstance = event.modal;

        // Appliquer le thème public si on est dans la section publique
        const currentAppSection = this.store.selectSignal(selectAppSection)();
        if (currentAppSection === AppSection.PUBLIC) {
          componentRef.location.nativeElement.classList.add('public-theme');
        }

        this.modalService.attachRef(event.modal.id, componentRef);
      } else if (event.modal) {
        this.viewContainerRef.remove(event.modal.id);
      }
    });

    this.setupOpenGraph();
  }

  loadCollections() {
    this.store.dispatch(eventTypesActions.findAll());
    this.store.dispatch(amenitiesActions.findAll());
    this.store.dispatch(layoutsActions.findAll());
  }

  setupOpenGraph() {
    this.meta.addTags([
      { property: 'og:locale', content: 'fr' },
      { property: 'og:type', content: 'article' },
      {
        property: 'og:url',
        content: 'https://www.location-salles.ch' + this.router.url,
      },
      { property: 'og:site_name', content: 'Location de salles' },
      {
        property: 'og:image',
        content: '/assets/img/open-graph.png',
      },
    ]);
  }
}
